import React, { useState, useEffect } from 'react';
import { Save, Calendar, Building2, Phone, MapPin, Image as ImageIcon, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { TaxSettings, PayPeriodSettings } from '../types';
import { usePayPeriodSettings } from '../hooks/usePayPeriodSettings';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { parseLocalDateForDisplay } from '../utils/payrollPeriodCalculator';

const defaultSettings: TaxSettings = {
  medicareRate: 1.45,
  socialSecurityRate: 6.2
};

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'company' | 'tax-rates' | 'pay-period' | 'valmar' | 'tax-forms'>('company');
  const [settings, setSettings] = useState<TaxSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { settings: payPeriodSettings, loading: payPeriodLoading, refetch: refetchPayPeriodSettings } = usePayPeriodSettings();
  const { settings: companySettings, loading: companyLoading, updateSettings: updateCompanySettings, uploadLogo } = useCompanySettings();
  const [editingPayPeriod, setEditingPayPeriod] = useState<Omit<PayPeriodSettings, 'id' | 'lastProcessed'>>({
    frequency: 'biweekly',
    periodStart: new Date().toISOString().split('T')[0],
    payday: 5
  });
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    displayLogo: false
  });
  const [valmarSettings, setValmarSettings] = useState({
    valmarUsername: '',
    valmarPassword: '',
    valmarEnvironment: 'sandbox' as 'sandbox' | 'production',
    valmarMerchantId: '',
    achEnabled: false
  });
  const [taxFormSettings, setTaxFormSettings] = useState({
    taxFormEin: '',
    taxFormLegalName: '',
    taxFormTradeName: '',
    taxFormAddress: ''
  });

  const weekdays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  useEffect(() => {
    if (payPeriodSettings) {
      setEditingPayPeriod({
        frequency: payPeriodSettings.frequency,
        periodStart: payPeriodSettings.periodStart,
        payday: payPeriodSettings.payday
      });
    }
  }, [payPeriodSettings]);

  useEffect(() => {
    if (companySettings) {
      setCompanyForm({
        companyName: companySettings.companyName,
        companyAddress: companySettings.companyAddress,
        companyPhone: companySettings.companyPhone,
        displayLogo: companySettings.displayLogo
      });
      setValmarSettings({
        valmarUsername: companySettings.valmarUsername || '',
        valmarPassword: companySettings.valmarPassword || '',
        valmarEnvironment: companySettings.valmarEnvironment || 'sandbox',
        valmarMerchantId: companySettings.valmarMerchantId || '',
        achEnabled: companySettings.achEnabled || false
      });
      setTaxFormSettings({
        taxFormEin: companySettings.taxFormEin || '',
        taxFormLegalName: companySettings.taxFormLegalName || '',
        taxFormTradeName: companySettings.taxFormTradeName || '',
        taxFormAddress: companySettings.taxFormAddress || ''
      });
    }
  }, [companySettings]);

  // Load existing settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('tax_settings')
          .select('*')
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          setSettings({
            medicareRate: data.medicare_rate,
            socialSecurityRate: data.social_security_rate
          });
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        // Don't show error to user since we're using default values
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // Save tax settings
      const { error: taxError } = await supabase
        .from('tax_settings')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records

      if (taxError) throw taxError;

      const { error: insertError } = await supabase
        .from('tax_settings')
        .insert({
          medicare_rate: settings.medicareRate,
          social_security_rate: settings.socialSecurityRate
        });

      if (insertError) throw insertError;

      // Handle pay period settings
      if (payPeriodSettings?.id) {
        // Update existing settings
        const { error: payPeriodError } = await supabase
          .from('pay_period_settings')
          .update({
            frequency: editingPayPeriod.frequency,
            period_start: editingPayPeriod.periodStart,
            payday: editingPayPeriod.payday
          })
          .eq('id', payPeriodSettings.id);

        if (payPeriodError) throw payPeriodError;
      } else {
        // Create new settings
        const { error: createError } = await supabase
          .from('pay_period_settings')
          .insert({
            frequency: editingPayPeriod.frequency,
            period_start: editingPayPeriod.periodStart,
            payday: editingPayPeriod.payday
          });

        if (createError) throw createError;
      }

      // Update company settings including Valmar and tax form settings
      await updateCompanySettings({
        companyName: companyForm.companyName,
        companyAddress: companyForm.companyAddress,
        companyPhone: companyForm.companyPhone,
        displayLogo: companyForm.displayLogo,
        valmarUsername: valmarSettings.valmarUsername,
        valmarPassword: valmarSettings.valmarPassword,
        valmarEnvironment: valmarSettings.valmarEnvironment,
        valmarMerchantId: valmarSettings.valmarMerchantId,
        achEnabled: valmarSettings.achEnabled,
        taxFormEin: taxFormSettings.taxFormEin,
        taxFormLegalName: taxFormSettings.taxFormLegalName,
        taxFormTradeName: taxFormSettings.taxFormTradeName,
        taxFormAddress: taxFormSettings.taxFormAddress
      });

      await refetchPayPeriodSettings();
      setSuccess(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      await uploadLogo(file);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    }
  };

  if (payPeriodLoading || companyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Settings</h2>

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">Settings saved successfully</div>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('company')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'company'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="h-5 w-5 inline-block mr-2" />
            Company
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tax-rates')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tax-rates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tax Rates
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('pay-period')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pay-period'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calendar className="h-5 w-5 inline-block mr-2" />
            Pay Period
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('valmar')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'valmar'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ACH Payments
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tax-forms')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tax-forms'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="h-5 w-5 inline-block mr-2" />
            Tax Forms
          </button>
        </nav>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {activeTab === 'company' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={companyForm.companyName}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, companyName: e.target.value }))}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Company Address</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={companyForm.companyAddress}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, companyAddress: e.target.value }))}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Company Phone</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  value={companyForm.companyPhone}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, companyPhone: e.target.value }))}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Company Logo</label>
              <div className="mt-1 flex items-center space-x-4">
                {companySettings?.logoUrl && (
                  <img
                    src={companySettings.logoUrl}
                    alt="Company Logo"
                    className="h-12 w-12 object-contain"
                  />
                )}
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                  <span>Upload Logo</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </label>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="display-logo"
                checked={companyForm.displayLogo}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, displayLogo: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="display-logo" className="ml-2 block text-sm text-gray-900">
                Display logo instead of company name on documents
              </label>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'tax-rates' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tax Rates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Medicare Tax Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={settings.medicareRate}
                onChange={(e) => setSettings({
                  ...settings,
                  medicareRate: parseFloat(e.target.value) || 0
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Social Security Tax Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={settings.socialSecurityRate}
                onChange={(e) => setSettings({
                  ...settings,
                  socialSecurityRate: parseFloat(e.target.value) || 0
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
        )}

        {activeTab === 'pay-period' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pay Period Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Pay Frequency</label>
              <select
                value={editingPayPeriod.frequency}
                onChange={(e) => setEditingPayPeriod(prev => ({
                  ...prev,
                  frequency: e.target.value as PayPeriodSettings['frequency']
                }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="semimonthly">Semi-monthly (1st and 15th)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">First Pay Period Start</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  value={editingPayPeriod.periodStart}
                  onChange={(e) => setEditingPayPeriod(prev => ({
                    ...prev,
                    periodStart: e.target.value
                  }))}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="payday" className="block text-sm font-medium text-gray-700">Payday</label>
            <select
              id="payday"
              value={editingPayPeriod.payday}
              onChange={(e) => setEditingPayPeriod(prev => ({
                ...prev,
                payday: parseInt(e.target.value)
              }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {weekdays.map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>

          {payPeriodSettings?.lastProcessed && (
            <div className="mt-4 text-sm text-gray-500">
              <p>Last Processed: {parseLocalDateForDisplay(payPeriodSettings.lastProcessed).toLocaleDateString()}</p>
            </div>
          )}
        </div>
        )}

        {activeTab === 'valmar' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Valmar ACH Payment Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="ach-enabled"
                checked={valmarSettings.achEnabled}
                onChange={(e) => setValmarSettings(prev => ({ ...prev, achEnabled: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="ach-enabled" className="ml-2 block text-sm text-gray-900">
                Enable ACH Direct Deposit Payments
              </label>
            </div>

            {valmarSettings.achEnabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Environment</label>
                  <select
                    value={valmarSettings.valmarEnvironment}
                    onChange={(e) => setValmarSettings(prev => ({ ...prev, valmarEnvironment: e.target.value as 'sandbox' | 'production' }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Merchant ID</label>
                  <input
                    type="text"
                    value={valmarSettings.valmarMerchantId}
                    onChange={(e) => setValmarSettings(prev => ({ ...prev, valmarMerchantId: e.target.value }))}
                    placeholder="Enter your Valmar Merchant ID"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <input
                    type="text"
                    value={valmarSettings.valmarUsername}
                    onChange={(e) => setValmarSettings(prev => ({ ...prev, valmarUsername: e.target.value }))}
                    placeholder="Enter your Valmar username"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={valmarSettings.valmarPassword}
                    onChange={(e) => setValmarSettings(prev => ({ ...prev, valmarPassword: e.target.value }))}
                    placeholder="Enter your Valmar password"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Your credentials are encrypted and stored securely. Get your login credentials from Valmar.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Setup Instructions:</strong>
                  </p>
                  <ol className="mt-2 text-sm text-blue-700 list-decimal list-inside space-y-1">
                    <li>Contact your Valmar account manager to obtain your username and password</li>
                    <li>Enter your Merchant ID (provided by Valmar)</li>
                    <li>Start with Sandbox mode for testing</li>
                    <li>Configure employee banking information in the Banking section</li>
                    <li>Switch to Production mode when ready for live payments</li>
                  </ol>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {activeTab === 'tax-forms' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tax Form Settings</h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure the information that will appear on tax forms (Form 940, W-2, etc.)
            </p>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Employer Identification Number (EIN)
                </label>
                <input
                  type="text"
                  value={taxFormSettings.taxFormEin}
                  onChange={(e) => setTaxFormSettings(prev => ({ ...prev, taxFormEin: e.target.value }))}
                  placeholder="XX-XXXXXXX"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Format: XX-XXXXXXX</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Legal Business Name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={taxFormSettings.taxFormLegalName}
                    onChange={(e) => setTaxFormSettings(prev => ({ ...prev, taxFormLegalName: e.target.value }))}
                    placeholder="Enter legal business name"
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Official legal name as registered with the IRS
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Trade Name (DBA)
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={taxFormSettings.taxFormTradeName}
                    onChange={(e) => setTaxFormSettings(prev => ({ ...prev, taxFormTradeName: e.target.value }))}
                    placeholder="Enter trade name (optional)"
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Doing Business As name, if different from legal name
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Business Mailing Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    value={taxFormSettings.taxFormAddress}
                    onChange={(e) => setTaxFormSettings(prev => ({ ...prev, taxFormAddress: e.target.value }))}
                    placeholder="Enter full business address including street, city, state, and zip code"
                    rows={3}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Complete mailing address for tax forms and IRS correspondence
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> This information will be used on all tax forms including Form 940, 941, W-2, and other IRS documents. Ensure all information is accurate and matches your IRS records.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Save className="h-4 w-4" />
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;